// Import react elements
import { useMemo, useState } from 'react';

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";

import useCustomProductsData from "../../hooks/useCustomProductsData";

// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";
import { SettingsEditor } from "../../manage_MMMs/components/MMMSettings";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { CloseIcon } from 'global/strich_icons/StrichIcons';

// Convert a settings object into the format used by the settings editor
const settingsObjectToArray = (settings) => {

    // Return an empty list if there are no settings
    if (!settings || typeof settings !== "object" || Array.isArray(settings)) { return []; }

    // Convert each setting into a single-key object
    return Object.entries(settings).map(([key, value]) => ({ [key]: value }));

};

// Convert the settings editor values back into a settings object
const cleanSettingsArray = (settingsArray) => {

    // Start with an empty settings object
    const settings = {};

    // Normalise the editable settings
    const rows = Array.isArray(settingsArray) ? settingsArray : [];

    // Loop through each editable setting
    for (const row of rows) {

        // Read and clean the setting key
        const keyRaw = Object.keys(row || {})[0];
        const key = keyRaw === undefined ? "" : String(keyRaw).trim();

        // Read and clean the setting value
        const valueRaw = keyRaw === undefined ? undefined : row[keyRaw];
        const value = valueRaw === null || valueRaw === undefined ? "" : typeof valueRaw === "string" ? valueRaw.trim() : valueRaw;

        // Ignore incomplete settings
        if (key === "" || value === "") { continue; }

        // Add the setting
        settings[key] = value;

    }

    // Return null if all overrides have been removed
    if (Object.keys(settings).length === 0) { return null; }

    // Return the stored print layout settings
    return settings;

};

// Render print area details on print area details page
const RenderPrintLayoutPopup = ({
    isOpen, 
    onClose, 
    printLayoutData, 
    handleDeletePrintLayout,
    selectedCustomProduct,
    setSelectedCustomProduct,
    setSelectedPrintLayout,
    handleOpenCustomizrClick,
    isCustomizrReady,
}) => {


    // Define toast options
    const { toastError } = useToast();


    // Store details
    const [isLoading, setIsLoading] = useState(null);


    // Custom hook for fetching and managing product data
    const {
        updatePrintLayout
    } = useCustomProductsData(); 
    
    // Build the editable print layout values
    const initialFormValues = useMemo(() => {

        // Copy the selected print layout
        const values = printLayoutData ? { ...printLayoutData } : {};

        // Add only the settings stored directly against this print layout
        values.settings_overrides_editor = settingsObjectToArray(printLayoutData?.settings_overrides);

        // Return the completed form values
        return values;

    }, [printLayoutData]);


    // Use the form handler for this single object
    const {

        // Local form values for this specific surface
        formValues,      

        // Local change handler
        handleChange,    

        isFormPartlyEdited,

    } = useFormHandler({

        // Initial values are the print layout values prepared above
        initialValues: initialFormValues,

        // Sync when `surface` prop changes (e.g. editing a different item)
        syncInitialValues: true,
        convertBooleans: true,
        convertNumbers: true,
        
        // Optional validation - only when using handleSubmit 
        validate: (values) => {

        }

    });

    // Close the print layout editor, warning first if anything has changed
    const handleClosePrintLayout = () => {

        // Close immediately when there is nothing to discard
        if (!isFormPartlyEdited()) {

            onClose();

            return;

        }

        // Confirm before discarding unsaved print layout changes
        DisplayConfirmationDialog(

            "You have unsaved changes. Are you sure you want to close?",
            "Close",
            "Keep Editing",

            async function (is_confirmed) {

                if (is_confirmed) {

                    onClose();

                }

            }

        );

    };

    // Handle local form submit (in it's own file so it doesn't get confused with formValues in parent CustomProduct.js)
    const handleSubmitSavePrintLayout = async (event) => {

        // Set loading state
        setIsLoading(true);

        // Prevent default browser submit behaviour
        event.preventDefault();

        // Prepare the settings stored directly against this print layout
        const settingsOverrides = cleanSettingsArray(formValues.settings_overrides_editor);

        // Build only the print layout fields editable in this popup
        const printLayoutPayload = {
            production_title: formValues.production_title,
            mockup_id: formValues.mockup_id,
            settings: settingsOverrides
        };

        // Make the call
        const response = await updatePrintLayout(selectedCustomProduct.code, printLayoutData.id, printLayoutPayload, setSelectedCustomProduct);
        
        // If parent returns a success flag
        if (response.success) {

            // Make the freshly loaded print layout the new editor baseline
            if (response.print_layout) { setSelectedPrintLayout(response.print_layout); }

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not update Print Layout: " + response.error_message });

        }

        // Clear loading state
        setIsLoading(false);

        // If parent indicates failure, do nothing so the values stay on screen

    };



    // If the pop up isn't open return early
    if (!isOpen) return null;



    return (

        <div className="popup-overlay">
            
            <div className="popup-container popup-container--small" style={{height: "auto"}}>

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Print Layout

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            {printLayoutData?.production_title} 

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => handleClosePrintLayout()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>


                <div className="popup-content popup-content__body">

                    <div>

                        <form id="printLayoutDetailsForm" onSubmit={handleSubmitSavePrintLayout}>

                            <TwoColumnList 
                                property={<label>Production Title</label>} 
                                value={<input 
                                    name="production_title" 
                                    type="text" 
                                    placeholder="Production Title" 
                                    value={formValues?.production_title || ""}
                                    onChange={handleChange}
                                    required
                                />} 
                            />

                            <TwoColumnList 
                                property={<label>Code</label>} 
                                value={<input 
                                    name="code" 
                                    type="text" 
                                    placeholder="Code"
                                    value={formValues?.code || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={printLayoutData}
                                />} 
                            />

                            <br/>

                            {formValues.settings && 
                    
                                <>

                                    <p><b>Resolved Settings</b></p>
                                    <br/>

                                    <div className="print-area-details__settings">
                                    
                                        {Object.entries(formValues?.settings).map(([key, value]) => {

                                            return (

                                                <div className="settings-list__item" key={key}>

                                                    <span className="settings-list__key">{key}</span><span className="settings-list__value">{value || ""} </span>

                                                </div>

                                            )
                                            
                                        })}

                                    </div>

                                </>
                                                
                            }

                            <br/>

                            <p><b>Print Layout Overrides</b></p>

                            <SettingsEditor
                                settingsArray={formValues.settings_overrides_editor || []}
                                onSettingsChange={(settingsArray) =>
                                    handleChange({
                                        target: {
                                            name: "settings_overrides_editor",
                                            value: settingsArray,
                                        },
                                    })
                                }
                                disabled={isLoading}
                                watchKey={printLayoutData?.id}
                            />

                            <br/> 

                            <p><b>Selected Mockup</b></p>
                            
                            <div className="print-layout__mockups-select">

                                {selectedCustomProduct.mockups?.map((mockup) => {

                                    return (

                                        <div
                                            key={mockup.id}
                                            className={`print-layout__mockups-image ${formValues.mockup_id === mockup.id ? "print-layout__mockups-image--selected" : ""}`} 
                                            
                                            onClick={() =>
                                                handleChange({
                                                    target: {
                                                        name: "mockup_id",
                                                        value: mockup.id,
                                                    },
                                                })
                                            }
                                            
                                        >

                                            <img src={mockup.mockup_thumbnail_url || mockup.mockup_image_url} alt={mockup.id}></img>

                                        </div>

                                    )

                                })}

                            </div>

                        </form>
                

                        {/* Tab content */}
                        {/* Scrollable content area */}
                        <div className="tabs-scroll-container">

                            <div className="tabs-content">


                            </div>

                        </div>

                        {/* Footer actions */}
                        <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                            <div>

                                {/* Only show delete button if we have positionData, if we don't then they're adding a new one */}
                                {printLayoutData &&
                                
                                    <CreateButton title="Delete" isDelete={true} tooltip="Delete Position" size="m" icon="/images/icon_bin.png" onClick={() => handleDeletePrintLayout(printLayoutData)} disabled={isLoading} />

                                }

                            </div>

                            <div className="horizontal-align-content-center">

                                <CreateButton
                                    type="button"
                                    title={isCustomizrReady ? "Edit in Customizr" : "Loading Customizr..."}
                                    size="m"
                                    onClick={() => {
                                        onClose();
                                        handleOpenCustomizrClick("printlayout", printLayoutData.id);
                                    }}
                                    disabled={isLoading || !isCustomizrReady || isFormPartlyEdited()}
                                />

                                <CreateButton
                                    type="submit"
                                    title="Save"
                                    size="m"
                                    formID="printLayoutDetailsForm"
                                    disabled={isLoading || !isFormPartlyEdited()}
                                />

                            </div>
                            
                        </div>
                        
                    </div>

                </div>

            </div>

        </div>

    );

}; export default RenderPrintLayoutPopup;
